import { DestroyRef, Directive, ElementRef, HostListener, inject, input, NgZone, OnDestroy, output, ViewContainerRef } from '@angular/core';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DsContextMenuComponent } from './ds-context-menu.component';
import { DsContextMenuTrigger } from './ds-context-menu.types';
import { CeOverlayContainerService } from '../ce-overlay/ce-overlay-container.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { filter, Subject, take, takeUntil, tap } from 'rxjs';

export type ContextMenuPosition =
    | 'top-left'
    | 'top-middle'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-middle'
    | 'bottom-right'
    | 'above'
    | 'below'
    | 'left'
    | 'right'
    | undefined;

/**
 * Helper function to create a position configuration.
 */
const createPosition = (
    originX: 'start' | 'center' | 'end',
    originY: 'top' | 'center' | 'bottom',
    overlayX: 'start' | 'center' | 'end',
    overlayY: 'top' | 'center' | 'bottom',
    offsetX = 0,
    offsetY = 0
): ConnectedPosition => ({
    originX,
    originY,
    overlayX,
    overlayY,
    offsetX,
    offsetY,
});

/**
 * Position strategy configurations for context menu positioning.
 * Each position has a primary placement and a fallback.
 */
const POSITION_STRATEGIES: Record<string, ConnectedPosition[]> = {
    above: [
        createPosition('center', 'top', 'center', 'bottom', 0, -8), // Above
        createPosition('center', 'bottom', 'center', 'top', 0, 8), // Fallback: Below
    ],
    below: [
        createPosition('center', 'bottom', 'center', 'top', 0, 8), // Below
        createPosition('center', 'top', 'center', 'bottom', 0, -8), // Fallback: Above
    ],
    left: [
        createPosition('start', 'center', 'end', 'center', -8, 0), // Left
        createPosition('end', 'center', 'start', 'center', 8, 0), // Fallback: Right
    ],
    right: [
        createPosition('end', 'center', 'start', 'center', 0, 0), // Right (submenu - 0 offset)
        createPosition('start', 'center', 'end', 'center', 0, 0), // Fallback: Left (submenu - 0 offset)
    ],
    'top-left': [
        createPosition('start', 'top', 'start', 'bottom', 0, -8),
        createPosition('start', 'bottom', 'start', 'top', 0, 8),
    ],
    'top-middle': [
        createPosition('center', 'top', 'center', 'bottom', 0, -8),
        createPosition('center', 'bottom', 'center', 'top', 0, 8),
    ],
    'top-right': [
        createPosition('end', 'top', 'end', 'bottom', 0, -8),
        createPosition('end', 'bottom', 'end', 'top', 0, 8),
    ],
    'bottom-left': [
        createPosition('start', 'bottom', 'start', 'top', 0, 8),
        createPosition('start', 'top', 'start', 'bottom', 0, -8),
    ],
    'bottom-middle': [
        createPosition('center', 'bottom', 'center', 'top', 0, 8),
        createPosition('center', 'top', 'center', 'bottom', 0, -8),
    ],
    'bottom-right': [
        createPosition('end', 'bottom', 'end', 'top', 0, 8),
        createPosition('end', 'top', 'end', 'bottom', 0, -8),
    ],
    default: [
        createPosition('start', 'bottom', 'start', 'top', 0, 8), // Position below target
        createPosition('start', 'top', 'start', 'bottom', 0, -8), // Position above target
        createPosition('end', 'bottom', 'end', 'top', 0, 8), // Position right of target
        createPosition('end', 'top', 'start', 'bottom', 0, -8), // Position left of target
    ],
};

/** Module-scoped counter for generating unique menu IDs. */
let nextMenuUniqueId = 0;

@Directive({
    selector: `[ds-context-menu-trigger-for], [dsContextMenuTriggerFor]`,
    host: {
        class: 'ds-context-menu-trigger',
    },
    exportAs: 'dsContextMenuTrigger',
})
/**
 * Directive for triggering and managing context menus with flexible positioning.
 *
 * @example
 * ```html
 * <button [dsContextMenuTriggerFor]="menu" dsContextMenuPosition="top-right">
 *   Actions
 * </button>
 * <ds-context-menu #menu>
 *   <ds-list-item>Action 1</ds-list-item>
 * </ds-context-menu>
 * ```
 */
export class DsContextMenuTriggerDirective implements OnDestroy, DsContextMenuTrigger {
    #menuOpen = false;
    #overlayRef: OverlayRef | null = null;
    #contextMenuOverlayContainer: HTMLDivElement | null = null;
    #uniqueId!: string;

    /**
     * Emits when the menu is closed, cancelling all per-open subscriptions
     * (backdropClick, router events) without waiting for directive destroy.
     */
    readonly #menuClosed$ = new Subject<void>();

    /** Tracks pending setTimeout IDs so they can be cancelled on destroy. */
    #pendingClickOutsideTimeout: ReturnType<typeof setTimeout> | null = null;

    readonly #viewContainerRef = inject(ViewContainerRef);
    readonly #overlayContainerService = inject(CeOverlayContainerService);
    readonly #overlay = inject(Overlay);
    readonly #element = inject(ElementRef<HTMLElement>);
    readonly #router = inject(Router);
    readonly #destroyRef = inject(DestroyRef);
    readonly #ngZone = inject(NgZone);
    readonly #boundMenuMouseLeave = this.handleMenuMouseLeave.bind(this);
    readonly #boundClickOutside = this.#onClickOutside.bind(this);
    readonly #boundOverlayKeydown = this.#onOverlayKeydown.bind(this);
    readonly #boundOverlayClick = this.#onOverlayClick.bind(this);
    readonly #boundMenuClick = () => this.closeMenu(false, 'item-click');
    readonly #boundDocumentEscape = this.#onDocumentEscape.bind(this);
    readonly #boundDocumentEnter = this.#onDocumentEnter.bind(this);

    /**
     * The container scope for the context menu overlay.
     * When set to 'dialog', the menu will be positioned within a dialog overlay container.
     * When empty, the menu will be appended to the document body.
     *
     * @default ''
     * @example
     * ```html
     * <button [dsContextMenuTriggerFor]="menu" dsContextMenuContainer="dialog">
     *   Actions
     * </button>
     * ```
     */
    readonly dsContextMenuContainer = input<string>('');

    /**
     * The menu component to be displayed when the trigger is activated.
     *
     * @example
     * ```html
     * <button [dsContextMenuTriggerFor]="menu">Actions</button>
     * <ds-context-menu #menu>
     *   <ds-list-item>Action 1</ds-list-item>
     * </ds-context-menu>
     * ```
     */
    readonly menu = input.required<DsContextMenuComponent>({
        alias: 'dsContextMenuTriggerFor',
    });

    /**
     * Event emitted when the context menu is opened or closed.
     * Emits true when opened, false when closed.
     */
    readonly opened = output<boolean>();

    /**
     * The position where the context menu should appear relative to the trigger element.
     *
     * @default undefined (uses 'default' position strategy)
     * @example
     * ```html
     * <button [dsContextMenuTriggerFor]="menu" dsContextMenuPosition="top-right">
     *   Actions
     * </button>
     * ```
     */
    readonly dsContextMenuPosition = input<ContextMenuPosition>(undefined);

    /**
     * Horizontal offset in pixels to adjust the menu position.
     * Positive values move the menu right, negative values move it left.
     *
     * @default 0
     * @example
     * ```html
     * <button [dsContextMenuTriggerFor]="menu" [dsContextMenuPositionOffsetX]="-20">
     *   Actions
     * </button>
     * ```
     */
    readonly dsContextMenuPositionOffsetX = input<number>(0);

    /**
     * Vertical offset in pixels to adjust the menu position.
     * Positive values move the menu down, negative values move it up.
     *
     * @default 0
     * @example
     * ```html
     * <button [dsContextMenuTriggerFor]="menu" [dsContextMenuPositionOffsetY]="20">
     *   Actions
     * </button>
     * ```
     */
    readonly dsContextMenuPositionOffsetY = input<number>(0);

    /**
     * Whether the context menu should open on hover instead of click.
     *
     * @default false
     * @example
     * ```html
     * <button [dsContextMenuTriggerFor]="menu" [dsContextMenuTriggerOnHover]="true">
     *   Hover Me
     * </button>
     * ```
     */
    readonly dsContextMenuTriggerOnHover = input<boolean>(false);

    @HostListener('click', ['$event'])
    /**
     * Handles click events on the trigger element.
     * Toggles the menu open/closed state.
     *
     * @param event - The mouse click event
     */
    handleClick(event: MouseEvent) {
        event.stopPropagation();
        if (this.#menuOpen) {
            this.closeMenu(false);
        } else {
            this.openMenu();
        }
    }

    @HostListener('keydown.enter', ['$event'])
    @HostListener('keydown.space', ['$event'])
    /**
     * Handles keyboard events on the trigger element.
     * Opens/closes the menu on Enter or Space key press.
     *
     * @param event - The keyboard event
     */
    handleKeydown(event: KeyboardEvent) {
        event.preventDefault();
        event.stopPropagation();
        if (this.#menuOpen) {
            this.closeMenu(false);
        } else {
            this.openMenu();
            this.menu().focusFirstItem();
        }
    }

    @HostListener('keydown.arrowright', ['$event'])
    /**
     * Handles ArrowRight keyboard event on the trigger element.
     * Opens submenu when ArrowRight is pressed on a menu item.
     *
     * @param event - The keyboard event
     */
    handleArrowRight(event: KeyboardEvent) {
        event.preventDefault();
        event.stopPropagation();
        if (!this.#menuOpen) {
            this.openMenu();
            this.menu().focusFirstItem();
        }
    }

    /**
     * Handles keydown events on the overlay element and delegates to the menu component.
     * Since the menu content is rendered in a CDK overlay portal (outside the
     * ds-context-menu host element's DOM tree), the component's host element never
     * receives keyboard events. This handler bridges that gap by forwarding events
     * from the overlay to the menu component's handleKeydown method.
     */
    #onOverlayKeydown(event: KeyboardEvent): void {
        this.menu()?.handleKeydown(event);
    }

    /**
     * Handles click events on the overlay element and delegates to the menu component.
     * Clicks fire for both mouse clicks and programmatic .click() calls
     * (e.g. from ds-list's Enter key → #handleEnterKeySelection → .click()).
     * This allows the menu to auto-close when a list item is activated.
     */
    #onOverlayClick(event: MouseEvent): void {
        this.menu()?.handleOverlayClick(event);
    }

    /**
     * Handles clicks outside the menu to close it.
     * Registered on document only while the menu is open.
     * Ignores clicks on the trigger button and inside the menu itself.
     */
    #onClickOutside(event: MouseEvent) {
        let targetElement = event.target as HTMLElement;

        // Check if click is on the trigger button itself
        if (this.#element.nativeElement.contains(targetElement)) return;

        // Traverse up the DOM tree
        while (targetElement !== null) {
            // Check if the current element is this specific menu instance
            if (targetElement.id === this.#uniqueId) return;
            targetElement = targetElement.parentElement as HTMLElement;
        }

        // Click event is outside a ds-context-menu element
        // Click inside the menu must be handled before closing the menu
        // so we need for event loop to finish before closing
        this.#pendingClickOutsideTimeout = setTimeout(() => {
            this.#pendingClickOutsideTimeout = null;
            this.#ngZone.run(() => this.closeMenu(false));
        }, 0);
    }

    /**
     * Handles Escape key press on the document.
     * Registered only while the menu is open to avoid per-instance document listeners.
     */
    #onDocumentEscape(event: KeyboardEvent): void {
        if (event.key !== 'Escape') return;
        if (!this.#menuOpen) return;
        if (event.defaultPrevented) return;

        if (this.#isFocusInsideMenu()) {
            event.preventDefault();
            event.stopPropagation();
            this.#ngZone.run(() => this.closeMenu(true));
        }
    }

    /**
     * Handles Enter key press on the document.
     * Registered only while the menu is open to avoid per-instance document listeners.
     * Only closes if Enter is pressed on the overlay container itself (not on menu items).
     */
    #onDocumentEnter(event: KeyboardEvent): void {
        if (event.key !== 'Enter') return;
        if (!this.#menuOpen) return;
        if (event.defaultPrevented) return;

        const thisMenuElement = document.getElementById(this.#uniqueId);
        const activeElement = document.activeElement as HTMLElement;

        if (thisMenuElement && thisMenuElement === activeElement) {
            event.preventDefault();
            event.stopPropagation();
            this.#ngZone.run(() => this.closeMenu(true));
        }
    }

    @HostListener('mouseenter', ['$event'])
    /**
     * Handles mouse enter events when hover trigger is enabled.
     * Opens the menu if triggerOnHover is true.
     *
     * @param $event - The mouse enter event
     */
    handleMouseEnter($event: MouseEvent) {
        $event.stopPropagation();
        if (this.dsContextMenuTriggerOnHover()) {
            this.openMenu();
        }
    }

    @HostListener('mouseleave', ['$event'])
    /**
     * Handles mouse leave events when hover trigger is enabled.
     * Closes the menu if the mouse moves outside both trigger and menu.
     *
     * @param $event - The mouse leave event
     */
    handleMouseLeave($event: MouseEvent) {
        $event.stopPropagation();
        const targetElement = $event.relatedTarget as HTMLElement;
        if (this.#isInsideSubMenu(targetElement)) {
            return;
        }

        // Click event is outside the context menu
        if (this.dsContextMenuTriggerOnHover()) {
            this.closeMenu(false);
        }
    }

    /**
     * Handles mouse leave events on the menu element itself.
     * Closes the menu when hover trigger is enabled and mouse leaves the menu.
     */
    handleMenuMouseLeave() {
        if (this.dsContextMenuTriggerOnHover()) {
            this.closeMenu(false);
        }
    }

    /**
     * Opens the context menu and positions it relative to the trigger element.
     * Handles overlay creation, event listeners, and routing cleanup.
     */
    openMenu(eventType?: string): void {
        const menuComponent = this.menu();
        if (!menuComponent) return;

        if (this.#overlayRef?.hasAttached()) {
            this.closeMenu(false);
        }

        // Dispose old overlay completely to avoid leaking OverlayRef instances
        if (this.#overlayRef) {
            this.#overlayRef.dispose();
            this.#overlayRef = null;
        }

        this.#createOverlayRef();

        // Use takeUntil to auto-unsubscribe when the menu closes
        this.#overlayRef!.backdropClick()
            .pipe(takeUntil(this.#menuClosed$))
            .subscribe(() => this.closeMenu(false));

        // Generate unique ID before attaching to prevent race conditions
        this.#uniqueId = `ds-context-menu-${nextMenuUniqueId++}`;

        const menuPortal = new TemplatePortal(menuComponent.templateRef, this.#viewContainerRef);

        this.#overlayRef!.attach(menuPortal);
        this.#overlayRef!.overlayElement.id = this.#uniqueId;
        this.#overlayRef!.overlayElement.setAttribute('tabindex', '-1');
        this.#menuOpen = true;

        // Emit opened event
        this.opened.emit(true);

        if (menuComponent.onMenuOpened) {
            menuComponent.onMenuOpened(true, eventType);
        }

        // Give the menu component a direct reference back to this trigger
        menuComponent.triggerDirective = this;
        // Set parent trigger element for submenu navigation
        // This allows the submenu to restore focus when closed with Escape
        menuComponent.parentTriggerElement = this.#element.nativeElement;

        // Register document-level listeners only while menu is open
        // This avoids N listeners per N directive instances on every keypress
        this.#ngZone.runOutsideAngular(() => {
            document.addEventListener('mouseup', this.#boundClickOutside);
            document.addEventListener('keydown', this.#boundDocumentEscape, true);
            document.addEventListener('keydown', this.#boundDocumentEnter, true);
        });

        // Add listeners to the menu element
        const menuElement = this.#overlayRef!.overlayElement;
        menuElement.addEventListener('mouseleave', this.#boundMenuMouseLeave);
        menuElement.addEventListener('keydown', this.#boundOverlayKeydown);
        menuElement.addEventListener('click', this.#boundOverlayClick);
        menuElement.addEventListener('click', this.#boundMenuClick);

        // Close menu on route changes — takeUntil ensures cleanup when menu closes
        this.#router.events
            .pipe(
                filter((event) => event instanceof NavigationStart),
                tap(() => this.closeMenu(false)),
                take(1),
                takeUntil(this.#menuClosed$),
                takeUntilDestroyed(this.#destroyRef)
            )
            .subscribe();
    }

    /**
     * Closes the context menu and cleans up all associated resources.
     * Removes event listeners, detaches the overlay, and clears the container.
     * Optionally restores focus to the trigger element.
     *
     * @param restoreFocus - Whether to return focus to the trigger button (default: false)
     */
    closeMenu(restoreFocus = false, eventType?: string): void {
        const menuComponent = this.menu();

        // Cancel all per-open subscriptions (backdropClick, router events)
        this.#menuClosed$.next();

        // Cancel any pending click-outside timeout
        if (this.#pendingClickOutsideTimeout !== null) {
            clearTimeout(this.#pendingClickOutsideTimeout);
            this.#pendingClickOutsideTimeout = null;
        }

        // Emit closed event
        this.opened.emit(false);

        if (menuComponent?.onMenuOpened) {
            menuComponent.onMenuOpened(false, eventType);
        }

        // Clear the back-reference to this trigger
        if (menuComponent) {
            menuComponent.triggerDirective = null;
        }

        // Deregister document-level listeners
        document.removeEventListener('mouseup', this.#boundClickOutside);
        document.removeEventListener('keydown', this.#boundDocumentEscape, true);
        document.removeEventListener('keydown', this.#boundDocumentEnter, true);

        // Remove event listeners before detaching
        if (this.#overlayRef?.overlayElement) {
            this.#overlayRef.overlayElement.removeEventListener('mouseleave', this.#boundMenuMouseLeave);
            this.#overlayRef.overlayElement.removeEventListener('keydown', this.#boundOverlayKeydown);
            this.#overlayRef.overlayElement.removeEventListener('click', this.#boundOverlayClick);
            this.#overlayRef.overlayElement.removeEventListener('click', this.#boundMenuClick);
        }

        if (this.#overlayRef?.hasAttached()) {
            this.#overlayRef.detach();
        }

        // Always clean up the overlay container if it exists
        if (this.#contextMenuOverlayContainer) {
            this.#overlayContainerService.removeOverlayContainer(this.#contextMenuOverlayContainer);
            this.#contextMenuOverlayContainer = null;
        }

        this.#menuOpen = false;

        // Restore focus to the trigger button if requested
        if (restoreFocus) {
            // Check if this is a submenu (trigger element has parentTriggerElement set on the menu)
            if (menuComponent?.parentTriggerElement) {
                menuComponent.parentTriggerElement.focus();
            } else {
                this.#element.nativeElement.focus();
            }
        }
    }

    /**
     * Checks if the current focus is inside this specific menu instance.
     *
     * @returns true if the active element is contained within this menu
     */
    #isFocusInsideMenu(): boolean {
        const activeElement = document.activeElement as HTMLElement;
        const thisMenuElement = document.getElementById(this.#uniqueId);
        return thisMenuElement?.contains(activeElement) ?? false;
    }

    #isInsideSubMenu(element: HTMLElement): boolean {
        // Traverse up the DOM tree to check if the element is inside a ds-context-menu
        while (element !== null) {
            if (element.id === this.#uniqueId) {
                return true;
            }
            element = element.parentElement as HTMLElement;
        }
        return false;
    }

    #createOverlayRef() {
        // Create overlay container based on configuration
        if (this.dsContextMenuContainer()) {
            this.#contextMenuOverlayContainer = this.#overlayContainerService.createOverlayContainerWithinContainer({
                className: 'context-menu-overlay-container',
                container: this.dsContextMenuContainer(),
            });
        } else {
            this.#contextMenuOverlayContainer = this.#overlayContainerService.createOverlayContainer(
                'context-menu-overlay-container'
            );
        }

        const positionStrategy = this.#overlay
            .position()
            .flexibleConnectedTo(this.#element)
            .withGrowAfterOpen(true)
            .withFlexibleDimensions(false)
            .withPositions(this.#getPositionStrategy(this.dsContextMenuPosition()));

        this.#overlayRef = this.#overlay.create({
            positionStrategy,
            scrollStrategy: this.#overlay.scrollStrategies.reposition(),
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-transparent-backdrop',
            panelClass: `ds-overlay-context-menu`,
            disposeOnNavigation: true,
        });

        this.#contextMenuOverlayContainer.appendChild(this.#overlayRef.hostElement);
    }

    /**
     * Gets the position strategy for the context menu based on the configured position.
     * Returns the appropriate connected positions with fallbacks, applying custom offsets if provided.
     *
     * @param position - The desired menu position
     * @returns Array of connected positions with primary and fallback placements
     */
    #getPositionStrategy(position?: ContextMenuPosition): ConnectedPosition[] {
        const basePositions = position
            ? POSITION_STRATEGIES[position] || POSITION_STRATEGIES['bottom-right']
            : POSITION_STRATEGIES['default'];

        // Get custom offsets from inputs
        const offsetX = this.dsContextMenuPositionOffsetX();
        const offsetY = this.dsContextMenuPositionOffsetY();

        // If no custom offsets, return base positions as-is
        if (offsetX === 0 && offsetY === 0) {
            return basePositions;
        }

        // Apply custom offsets to all position configurations
        return basePositions.map((pos) => ({
            ...pos,
            offsetX: (pos.offsetX ?? 0) + offsetX,
            offsetY: (pos.offsetY ?? 0) + offsetY,
        }));
    }

    ngOnDestroy(): void {
        this.closeMenu(false);

        // Complete the subject to prevent any future emissions
        this.#menuClosed$.next();
        this.#menuClosed$.complete();

        if (this.#overlayRef) {
            this.#overlayRef.dispose();
            this.#overlayRef = null;
        }
    }
}
